import React, { useState, useEffect } from 'react';
import { HiX, HiCloudUpload, HiClock } from './Icons';
import { createPromotion, updatePromotion, createCategory, uploadPromotionImage, setPromotionStatPrices } from '../services/api';
import type { Promotion, PromotionCategory, SocietySummary } from '../services/api';
import './PromotionForm.css';
import SocietyStepMap from './SocietyStepMap';

export type PromotionFormData = {
  title: string;
  description: string;
  image_url: string;
  video_url: string;
  promotion_type: 'story' | 'spotlight' | 'category';
  brand_name: string;
  discount_percentage: string;
  discount_amount: string;
  coupon_code: string;
  mobile_number: string;
  call_button_enabled: number;
  show_location: number;
  location_latitude: string;
  location_longitude: string;
  location_address: string;
  cover_photo_url: string;
  show_details_page: number;
  show_store_awning: number;
  action_button_required: number;
  action_button_text: string;
  action_button_link: string;
  action_button_on_card: number;
  valid_from: string;
  valid_to: string;
  is_active: number;
  display_order: number;
  click_url: string;
  category_ids: number[];
  society_ids: number[];
  image_urls: string[];
};

interface PromotionFormProps {
  promotion?: Promotion | null;
  categories: PromotionCategory[];
  societies: SocietySummary[];
  onClose: () => void;
  onSave: () => void;
  onFormChange?: (data: PromotionFormData) => void;
  inline?: boolean; // If true, render without modal overlay (for customer create-with-preview)
  /** When set (admin creating for a customer), societies are scoped to this customer and customer_id is sent on create */
  customerId?: number | null;
  /** When true, show and save pricing (credits per unit). Admin only; customers do not see or set pricing. */
  showPricingSection?: boolean;
}

const PromotionForm: React.FC<PromotionFormProps> = ({
  promotion,
  categories,
  societies,
  onClose,
  onSave,
  onFormChange,
  inline = false,
  customerId = null,
  showPricingSection = false,
}) => {
  const isAdmin = !!showPricingSection;
  const isEditing = !!promotion;
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    video_url: '',
    promotion_type: 'category' as 'story' | 'spotlight' | 'category',
    brand_name: '',
    discount_percentage: '',
    discount_amount: '',
    coupon_code: '',
    mobile_number: '',
    call_button_enabled: 0,
    show_location: 0,
    location_latitude: '',
    location_longitude: '',
    location_address: '',
    cover_photo_url: '',
    show_details_page: 0,
    show_store_awning: 0,
    action_button_required: 0,
    action_button_text: '',
    action_button_link: '',
    action_button_on_card: 0,
    valid_from: '',
    valid_to: '',
    is_active: 1,
    display_order: 0,
    click_url: '',
    category_ids: [] as number[],
    society_ids: [] as number[],
    image_urls: [] as string[], // Array for multiple images (up to 5)
    price_impressions: '',
    price_clicks: '',
    price_story_views: '',
    price_story_completes: '',
  });
  const [stepIndex, setStepIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCreateCategory, setShowCreateCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDesc, setNewCategoryDesc] = useState('');
  const [localCategories, setLocalCategories] = useState(categories);
  const [uploadingImages, setUploadingImages] = useState<Record<number, boolean>>({});
  const [imagePreviews, setImagePreviews] = useState<Record<number, string>>({});
  const [societyPincodeFilter, setSocietyPincodeFilter] = useState('');
  const [societyRangeKm, setSocietyRangeKm] = useState(5);
  const [societyCenter, setSocietyCenter] = useState<{ lat: number; lng: number } | null>(null);
  const [societyFilterError, setSocietyFilterError] = useState<string | null>(null);
  /** Fresh map subtree each time user opens Step 5 (avoids Leaflet "container already initialized" on remount). */
  const [societyMapInstanceKey, setSocietyMapInstanceKey] = useState(0);
  const wasOnSocietyStepRef = React.useRef(false);

  useEffect(() => {
    setLocalCategories(categories);
  }, [categories]);

  useEffect(() => {
    onFormChange?.(formData);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formData is the source of truth; onFormChange is stable
  }, [formData]);

  useEffect(() => {
    // When details page is disabled, keep only a single image (legacy image_url) and clear multi-image carousel.
    if (formData.show_details_page !== 1) {
      setFormData((prev) => {
        if ((prev.image_urls?.length ?? 0) === 0) return prev;
        return { ...prev, image_urls: [] };
      });
      setImagePreviews({});
    } else {
      // Ensure at least one image slot is present when details page is enabled (so the UI doesn't start empty).
      setFormData((prev) => {
        if (prev.image_urls && prev.image_urls.length > 0) return prev;
        return { ...prev, image_urls: [''] };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.show_details_page]);

  useEffect(() => {
    if (promotion) {
      // Load promotion images - use image_urls array if available, otherwise fallback to image_url
      const promotionWithExtras = promotion as Promotion & { image_urls?: string[]; society_ids?: number[]; category_ids?: number[] };
      
      // Get image_urls from API, or use empty array if not available
      const apiImageUrls = promotionWithExtras.image_urls || [];
      
      // If image_urls is empty but image_url exists, don't add it to image_urls array
      // Keep it separate so it shows in the legacy field
      const imageUrls = apiImageUrls.length > 0 ? apiImageUrls : [];
      
      // Get society_ids and category_ids from promotion (if available from API)
      const societyIds = promotionWithExtras.society_ids || [];
      const categoryIds = promotionWithExtras.category_ids || [];
      
      // If society_ids is empty, it means "All Society" is selected
      // Convert empty array to [-1] for UI display
      const displaySocietyIds = societyIds.length === 0 ? [-1] : societyIds;
      
      setFormData({
        title: promotion.title,
        description: promotion.description || '',
        image_url: promotion.image_url || '', // Keep legacy image_url separate
        video_url: promotion.video_url || '',
        promotion_type: promotion.promotion_type,
        brand_name: promotion.brand_name || '',
        discount_percentage: promotion.discount_percentage?.toString() || '',
        discount_amount: promotion.discount_amount?.toString() || '',
        coupon_code: promotion.coupon_code || '',
        mobile_number: promotion.mobile_number || '',
        call_button_enabled: promotion.call_button_enabled ?? 0,
        show_location: promotion.show_location ?? 0,
        location_latitude: promotion.location_latitude?.toString() || '',
        location_longitude: promotion.location_longitude?.toString() || '',
        location_address: promotion.location_address || '',
        cover_photo_url: promotion.cover_photo_url || '',
        show_details_page: promotion.show_details_page ?? 0,
        show_store_awning: (promotion as Promotion & { show_store_awning?: number }).show_store_awning ?? 0,
        action_button_required: (promotion as Promotion & { action_button_required?: number }).action_button_required ?? 0,
        action_button_text: (promotion as Promotion & { action_button_text?: string | null }).action_button_text || '',
        action_button_link: (promotion as Promotion & { action_button_link?: string | null }).action_button_link || '',
        action_button_on_card: (promotion as Promotion & { action_button_on_card?: number }).action_button_on_card ?? 0,
        valid_from: promotion.valid_from.split('T')[0],
        valid_to: promotion.valid_to.split('T')[0],
        is_active: promotion.is_active,
        display_order: promotion.display_order,
        click_url: promotion.click_url || '',
        category_ids: categoryIds,
        society_ids: displaySocietyIds, // Use -1 for "All Society" if empty
        image_urls: imageUrls, // Only use image_urls from API, not image_url
        price_impressions: (promotion as Promotion & { stat_prices?: { stat_parameter: string; price_per_unit: number }[] }).stat_prices?.find((s) => s.stat_parameter === 'impressions')?.price_per_unit?.toString() ?? '',
        price_clicks: (promotion as Promotion & { stat_prices?: { stat_parameter: string; price_per_unit: number }[] }).stat_prices?.find((s) => s.stat_parameter === 'clicks')?.price_per_unit?.toString() ?? '',
        price_story_views: (promotion as Promotion & { stat_prices?: { stat_parameter: string; price_per_unit: number }[] }).stat_prices?.find((s) => s.stat_parameter === 'story_views')?.price_per_unit?.toString() ?? '',
        price_story_completes: (promotion as Promotion & { stat_prices?: { stat_parameter: string; price_per_unit: number }[] }).stat_prices?.find((s) => s.stat_parameter === 'story_completes')?.price_per_unit?.toString() ?? '',
      });

      // Set previews for existing images from image_urls array
      const previews: Record<number, string> = {};
      imageUrls.forEach((url, index) => {
        if (url) {
          previews[index] = url;
        }
      });
      setImagePreviews(previews);
    } else {
      // Set default dates (today and 30 days from now)
      const today = new Date().toISOString().split('T')[0];
      const future = new Date();
      future.setDate(future.getDate() + 30);
      const futureStr = future.toISOString().split('T')[0];
      
      setFormData(prev => ({
        ...prev,
        valid_from: today,
        valid_to: futureStr,
      }));
    }
  }, [promotion]);

  const steps = [
    { key: 'basics', label: 'Promotion data' },
    { key: 'media', label: 'Media & visuals' },
    { key: 'offer', label: 'Offer details' },
    { key: 'actions', label: 'Actions' },
    { key: 'society', label: 'Society' },
    { key: 'categoryOrder', label: 'Category & order', adminOnly: false },
    { key: 'pricing', label: 'Pricing', adminOnly: true },
  ].filter((s) => (s as any).adminOnly ? isAdmin : true);

  const lastStepIndex = steps.length - 1;

  useEffect(() => {
    const onSociety = steps[stepIndex]?.key === 'society';
    if (onSociety && !wasOnSocietyStepRef.current) {
      setSocietyMapInstanceKey((k) => k + 1);
    }
    wasOnSocietyStepRef.current = onSociety;
    // steps comes from current render; only stepIndex should retrigger
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex]);

  const parseLatLong = (latlong: string | null | undefined): [number, number] | null => {
    if (!latlong || typeof latlong !== 'string') return null;
    const parts = latlong.split(',').map((p) => parseFloat(p.trim()));
    if (parts.length >= 2 && !Number.isNaN(parts[0]) && !Number.isNaN(parts[1])) {
      return [parts[0], parts[1]];
    }
    return null;
  };

  const distanceKm = (a: [number, number], b: [number, number]): number => {
    const R = 6371;
    const toRad = (n: number) => (n * Math.PI) / 180;
    const dLat = toRad(b[0] - a[0]);
    const dLon = toRad(b[1] - a[1]);
    const aa =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(aa), Math.sqrt(1 - aa));
    return R * c;
  };

  const societiesForStep = (() => {
    if (!societyCenter) return societies;
    return societies.filter((s) => {
      const pos = parseLatLong(s.latlong ?? null);
      if (!pos) return false;
      return distanceKm([societyCenter.lat, societyCenter.lng], pos) <= societyRangeKm;
    });
  })();

  const hasAnyImage = () => {
    const cover = (formData.cover_photo_url || '').trim();
    const legacy = (formData.image_url || '').trim();
    const multi = (formData.image_urls || []).some((u) => (u || '').trim() !== '');
    return !!(cover || legacy || multi);
  };

  const validateStep = (idx: number): string | null => {
    const key = steps[idx]?.key;
    if (!key) return null;

    if (key === 'basics') {
      if (!formData.title?.trim()) return 'Title is required.';
      if (!formData.valid_from) return 'Valid From is required.';
      if (!formData.valid_to) return 'Valid To is required.';
      return null;
    }

    if (key === 'media') {
      if (!hasAnyImage()) return 'Please provide at least one image (cover or image).';
      return null;
    }

    if (key === 'society') {
      if (!formData.society_ids || formData.society_ids.length === 0) {
        return 'Please select at least one society (or All Society).';
      }
      return null;
    }

    if (key === 'categoryOrder') {
      if (formData.promotion_type === 'category' && (!formData.category_ids || formData.category_ids.length === 0)) {
        return 'Please select at least one category for Category promotions.';
      }
      return null;
    }

    return null;
  };

  const goNext = () => {
    const msg = validateStep(stepIndex);
    if (msg) {
      setError(msg);
      return;
    }
    setError('');
    setStepIndex((i) => Math.min(lastStepIndex, i + 1));
  };

  const goBack = () => {
    setError('');
    setStepIndex((i) => Math.max(0, i - 1));
  };

  const jumpToStep = (idx: number) => {
    if (idx === stepIndex) return;
    // allow jumping backwards freely; for forward jump, validate current step first
    if (idx > stepIndex) {
      const msg = validateStep(stepIndex);
      if (msg) {
        setError(msg);
        return;
      }
    }
    setError('');
    setStepIndex(idx);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Before submit, validate all steps (high-signal ones).
      for (let i = 0; i < steps.length; i++) {
        const msg = validateStep(i);
        if (msg) {
          setStepIndex(i);
          throw new Error(msg);
        }
      }

      // Handle "All Society" option - if -1 is selected, send empty array to backend
      // Backend will interpret empty array as "visible to all societies"
      const societyIds = formData.society_ids.includes(-1) 
        ? [] // Empty array means visible to all societies
        : formData.society_ids.filter(id => id !== -1); // Remove -1 if somehow included

      const data: any = {
        ...formData,
        discount_percentage: formData.discount_percentage ? parseFloat(formData.discount_percentage) : undefined,
        discount_amount: formData.discount_amount ? parseFloat(formData.discount_amount) : undefined,
        location_latitude: formData.location_latitude ? parseFloat(formData.location_latitude) : undefined,
        location_longitude: formData.location_longitude ? parseFloat(formData.location_longitude) : undefined,
        call_button_enabled: formData.call_button_enabled,
        show_location: formData.show_location,
        cover_photo_url: formData.cover_photo_url || undefined,
        show_details_page: formData.show_details_page,
        action_button_required: formData.action_button_required,
        action_button_text: formData.action_button_text || undefined,
        action_button_link: formData.action_button_link || undefined,
        action_button_on_card: formData.action_button_on_card,
        show_store_awning: formData.show_store_awning,
        valid_from: new Date(formData.valid_from).toISOString(),
        valid_to: new Date(formData.valid_to).toISOString(),
        is_active: formData.is_active,
        display_order: formData.display_order,
        category_ids: formData.category_ids,
        society_ids: societyIds, // Send empty array for "All Society"
      };

      // Handle image_urls and image_url
      // Filter out empty strings from image_urls
      const validImageUrls = formData.image_urls ? formData.image_urls.filter(url => url && url.trim() !== '') : [];
      
      // Always include image_url in the request if it has a value
      // Backend checks for undefined, so we need to explicitly include it
      if (formData.image_url && formData.image_url.trim() !== '') {
        data.image_url = formData.image_url;
      } else {
        // Send null or empty string to clear it - backend will handle undefined check
        // But we need to explicitly set it so backend knows to update it
        data.image_url = formData.image_url || null;
      }
      
      // Send image_urls if it has valid URLs
      if (validImageUrls.length > 0) {
        data.image_urls = validImageUrls;
      } else {
        // Send empty array to clear image_urls
        data.image_urls = [];
      }

      let promotionId: number;
      if (promotion) {
        await updatePromotion(promotion.id, data);
        promotionId = promotion.id;
      } else {
        if (customerId != null) {
          (data as Record<string, unknown>).customer_id = customerId;
        }
        const createRes = await createPromotion(data);
        promotionId = createRes.data.id;
      }

      if (showPricingSection) {
        const prices: Array<{ stat_parameter: string; price_per_unit: number }> = [];
        const addPrice = (param: string, val: string) => {
          const n = parseFloat(val);
          if (!Number.isNaN(n) && n >= 0) prices.push({ stat_parameter: param, price_per_unit: n });
        };
        addPrice('impressions', formData.price_impressions);
        addPrice('clicks', formData.price_clicks);
        addPrice('story_views', formData.price_story_views);
        addPrice('story_completes', formData.price_story_completes);
        if (prices.length > 0) {
          await setPromotionStatPrices(promotionId, prices);
        }
      }

      onSave();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save promotion');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryToggle = (categoryId: number) => {
    setFormData(prev => ({
      ...prev,
      category_ids: prev.category_ids.includes(categoryId)
        ? prev.category_ids.filter(id => id !== categoryId)
        : [...prev.category_ids, categoryId],
    }));
  };

  const handleSocietyToggle = (societyId: number) => {
    // Special handling for "All Society" (id: -1)
    if (societyId === -1) {
      setFormData(prev => ({
        ...prev,
        // If "All Society" is selected, clear all other selections
        society_ids: prev.society_ids.includes(-1) ? [] : [-1],
      }));
    } else {
      setFormData(prev => {
        // If selecting a specific society, remove "All Society" if it's selected
        const newIds = prev.society_ids.includes(-1)
          ? prev.society_ids.filter(id => id !== -1)
          : prev.society_ids;
        
        return {
          ...prev,
          society_ids: newIds.includes(societyId)
            ? newIds.filter(id => id !== societyId)
            : [...newIds, societyId],
        };
      });
    }
  };

  const handleAddImageUrl = () => {
    if (formData.image_urls.length < 5) {
      setFormData(prev => ({
        ...prev,
        image_urls: [...prev.image_urls, ''],
      }));
    }
  };

  const handleImageUrlChange = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      image_urls: prev.image_urls.map((url, i) => i === index ? value : url),
    }));
  };

  const handleRemoveImageUrl = (index: number) => {
    setFormData(prev => ({
      ...prev,
      image_urls: prev.image_urls.filter((_, i) => i !== index),
    }));
    // Remove preview
    setImagePreviews(prev => {
      const newPreviews = { ...prev };
      delete newPreviews[index];
      return newPreviews;
    });
  };

  const handleImageUpload = async (index: number, file: File) => {
    // Validate file type
    const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validImageTypes.includes(file.type)) {
      setError('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (3MB limit)
    if (file.size > 3 * 1024 * 1024) {
      setError('Image size must be less than 3MB');
      return;
    }

    try {
      setUploadingImages(prev => ({ ...prev, [index]: true }));
      setError('');

      // Upload the file using promotion upload API (no societyId required)
      const uploadResponse = await uploadPromotionImage(file);
      
      // Use public URL from upload response (promotion images are made public)
      const imageUrl = uploadResponse.data.publicUrl || 
        `https://storage.googleapis.com/tappy_tray_society_documents/${uploadResponse.data.filePath}`;
      
      // Update the image URL at the specified index
      setFormData(prev => {
        const newUrls = [...prev.image_urls];
        // Ensure array is long enough
        while (newUrls.length <= index) {
          newUrls.push('');
        }
        newUrls[index] = imageUrl;
        return { ...prev, image_urls: newUrls };
      });

      // Set preview
      setImagePreviews(prev => ({
        ...prev,
        [index]: imageUrl,
      }));

    } catch (err: any) {
      setError(err.message || 'Failed to upload image');
    } finally {
      setUploadingImages(prev => ({ ...prev, [index]: false }));
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      setError('Category name is required');
      return;
    }

    try {
      const response = await createCategory({
        name: newCategoryName,
        description: newCategoryDesc || undefined,
      });
      setLocalCategories([...localCategories, response.data]);
      setNewCategoryName('');
      setNewCategoryDesc('');
      setShowCreateCategory(false);
      // Auto-select the new category
      setFormData(prev => ({
        ...prev,
        category_ids: [...prev.category_ids, response.data.id],
      }));
    } catch (err: any) {
      setError(err.message || 'Failed to create category');
    }
  };

  const formContent = (
    <>
        <div className="form-header">
          <h2>{promotion ? 'Edit Promotion' : 'Create Promotion'}</h2>
          {!inline && <button onClick={onClose} className="close-button" aria-label="Close"><HiX size={24} /></button>}
        </div>

        <form onSubmit={handleSubmit} className="promotion-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-stepper">
            {steps.map((s, idx) => (
              <button
                key={s.key}
                type="button"
                className={[
                  'stepper-item',
                  idx === stepIndex ? 'active' : '',
                  idx < stepIndex ? 'done' : '',
                ].join(' ')}
                onClick={() => jumpToStep(idx)}
              >
                <span className="stepper-index">{idx + 1}</span>
                <span className="stepper-label">{s.label}</span>
              </button>
            ))}
          </div>

          {/* STEP 1: Promotion data */}
          {steps[stepIndex]?.key === 'basics' && (
            <section className="form-section">
              <h3 className="form-section-title">Step 1: Fill the promotion data</h3>
              <p className="form-section-desc">
                Brand/business, type, title and dates. These drive where and how it renders.
              </p>
              <div className="form-group">
                <label>Brand / Business Name</label>
                <input
                  type="text"
                  value={formData.brand_name}
                  onChange={(e) => setFormData({ ...formData, brand_name: e.target.value })}
                  placeholder="e.g. Brand or Store Name"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Promotion Type *</label>
                  <select
                    value={formData.promotion_type}
                    onChange={(e) => setFormData({ ...formData, promotion_type: e.target.value as any })}
                    required
                  >
                    <option value="story">Story – swipeable cover + video/slides</option>
                    <option value="spotlight">Spotlight – hero card in Brand Spotlight</option>
                    <option value="category">Category – card inside a category section</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Show Business Page Details</label>
                  <label className="checkbox-inline">
                    <input
                      type="checkbox"
                      checked={formData.show_details_page === 1}
                      onChange={(e) => setFormData({ ...formData, show_details_page: e.target.checked ? 1 : 0 })}
                    />
                    Enable details page (carousel + content)
                  </label>
                  <p className="form-hint">
                    If enabled, you can add multiple images in the next step. If disabled, we’ll use a single image only.
                  </p>
                </div>
              </div>

              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  placeholder="Short text shown on cards or details page"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Valid From *</label>
                  <input
                    type="date"
                    value={formData.valid_from}
                    onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Valid To *</label>
                  <input
                    type="date"
                    value={formData.valid_to}
                    onChange={(e) => setFormData({ ...formData, valid_to: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Mobile Number</label>
                <input
                  type="tel"
                  value={formData.mobile_number}
                  onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value })}
                  placeholder="e.g. +919876543210"
                  maxLength={20}
                />
              </div>
            </section>
          )}

          {/* STEP 2: Media and visuals */}
          {steps[stepIndex]?.key === 'media' && (
            <section className="form-section">
              <h3 className="form-section-title">Step 2: Media and visuals</h3>
              <p className="form-section-desc">
                {formData.show_details_page === 1
                  ? 'Details page enabled: you can upload multiple images for the carousel.'
                  : 'Details page disabled: only a single image will be used.'}
              </p>

              <div className="form-group">
                <label>Cover / hero image URL</label>
                <input
                  type="url"
                  value={formData.cover_photo_url}
                  onChange={(e) => setFormData({ ...formData, cover_photo_url: e.target.value })}
                  placeholder="https://example.com/cover.jpg"
                />
              </div>

              <div className="form-group">
                <label>Video URL (optional)</label>
                <p className="form-section-desc">
                  {formData.promotion_type === 'story'
                    ? 'Story slide video. Also used when the story is opened from the carousel.'
                    : 'Short MP4 for in-card muted autoplay on Home. Use cover / hero image above as the poster while the video loads.'}
                  {' '}
                  If details page is off, tapping the card opens this video fullscreen with sound.
                </p>
                <input
                  type="url"
                  value={formData.video_url}
                  onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                  placeholder="https://example.com/promo.mp4"
                />
                {formData.video_url.trim() && (
                  <div className="video-preview-wrap">
                    <video
                      src={formData.video_url.trim()}
                      controls
                      muted
                      playsInline
                      preload="metadata"
                      className="video-preview"
                    />
                  </div>
                )}
              </div>

              {formData.show_details_page === 1 ? (
                <div className="form-group">
                  <label>Images (multiple) – upload or URL</label>
                  {formData.image_urls.map((url, index) => (
                    <div key={index} className="image-upload-row">
                      <div className="image-input-section">
                        {imagePreviews[index] && (
                          <div className="image-preview">
                            <img src={imagePreviews[index]} alt={`Preview ${index + 1}`} />
                            <button
                              type="button"
                              onClick={() => {
                                setImagePreviews((prev) => {
                                  const next = { ...prev };
                                  delete next[index];
                                  return next;
                                });
                              }}
                              className="remove-preview-btn"
                              title="Remove preview"
                              aria-label="Remove"
                            >
                              <HiX size={18} />
                            </button>
                          </div>
                        )}
                        <div className="image-input-group">
                          <input
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleImageUpload(index, file);
                            }}
                            className="file-input"
                            id={`image-upload-${index}`}
                            disabled={uploadingImages[index]}
                          />
                          <label
                            htmlFor={`image-upload-${index}`}
                            className={`upload-button ${uploadingImages[index] ? 'uploading' : ''}`}
                          >
                            {uploadingImages[index]
                              ? <><HiClock className="icon-spin" size={18} /> Uploading...</>
                              : <><HiCloudUpload size={18} /> Upload Image</>}
                          </label>
                          <span className="or-divider">OR</span>
                          <input
                            type="url"
                            value={url}
                            onChange={(e) => handleImageUrlChange(index, e.target.value)}
                            placeholder={`Enter image URL ${index + 1}`}
                            className="image-url-input"
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveImageUrl(index)}
                        className="remove-image-btn"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  {formData.image_urls.length < 5 && (
                    <button type="button" onClick={handleAddImageUrl} className="add-image-btn">
                      + Add Image ({formData.image_urls.length}/5)
                    </button>
                  )}
                </div>
              ) : (
                <div className="form-group">
                  <label>Single image (upload or URL)</label>
                  <div className="image-input-group">
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          // use index 0 upload handler, then store into legacy field
                          handleImageUpload(0, file);
                        }
                      }}
                      className="file-input"
                      id="single-image-upload"
                      disabled={uploadingImages[0]}
                    />
                    <label
                      htmlFor="single-image-upload"
                      className={`upload-button ${uploadingImages[0] ? 'uploading' : ''}`}
                    >
                      {uploadingImages[0]
                        ? <><HiClock className="icon-spin" size={18} /> Uploading...</>
                        : <><HiCloudUpload size={18} /> Upload Image</>}
                    </label>
                    <span className="or-divider">OR</span>
                    <input
                      type="url"
                      value={formData.image_url}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      placeholder="Enter image URL"
                      className="image-url-input"
                    />
                  </div>
                  <p className="form-hint">This image will be used on the card.</p>
                </div>
              )}

              <div className="form-group">
                <label>Click URL (external link)</label>
                <input
                  type="url"
                  value={formData.click_url}
                  onChange={(e) => setFormData({ ...formData, click_url: e.target.value })}
                  placeholder="https://example.com"
                />
              </div>
            </section>
          )}

          {/* STEP 3: Offer details */}
          {steps[stepIndex]?.key === 'offer' && (
            <section className="form-section">
              <h3 className="form-section-title">Step 3: Offer details</h3>
              <p className="form-section-desc">Discount and coupon shown on the cards in the app.</p>
              <div className="form-row">
                <div className="form-group">
                  <label>Discount Percentage</label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.discount_percentage}
                    onChange={(e) => setFormData({ ...formData, discount_percentage: e.target.value })}
                    placeholder="e.g. 20"
                  />
                </div>
                <div className="form-group">
                  <label>Discount Amount (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.discount_amount}
                    onChange={(e) => setFormData({ ...formData, discount_amount: e.target.value })}
                    placeholder="e.g. 100"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Coupon Code</label>
                <input
                  type="text"
                  value={formData.coupon_code}
                  onChange={(e) => setFormData({ ...formData, coupon_code: e.target.value.toUpperCase() })}
                  placeholder="e.g. SAVE20"
                  maxLength={50}
                />
              </div>
            </section>
          )}

          {/* STEP 4: Actions */}
          {steps[stepIndex]?.key === 'actions' && (
            <>
              <section className="form-section">
                <h3 className="form-section-title">Step 4: Add actions</h3>
                <p className="form-section-desc">Call / location on card, and optional admin-only accents.</p>
                <div className="form-row">
                  <div className="form-group">
                    <label className="checkbox-inline">
                      <input
                        type="checkbox"
                        checked={formData.call_button_enabled === 1}
                        onChange={(e) => setFormData({ ...formData, call_button_enabled: e.target.checked ? 1 : 0 })}
                      />
                      Show Call Button
                    </label>
                  </div>
                  <div className="form-group">
                    <label className="checkbox-inline">
                      <input
                        type="checkbox"
                        checked={formData.show_location === 1}
                        onChange={(e) => setFormData({ ...formData, show_location: e.target.checked ? 1 : 0 })}
                      />
                      Show Location
                    </label>
                  </div>
                </div>
                {formData.show_location === 1 && (
                  <>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Location Latitude</label>
                        <input
                          type="number"
                          step="any"
                          value={formData.location_latitude}
                          onChange={(e) => setFormData({ ...formData, location_latitude: e.target.value })}
                          placeholder="e.g. 28.6139"
                        />
                      </div>
                      <div className="form-group">
                        <label>Location Longitude</label>
                        <input
                          type="number"
                          step="any"
                          value={formData.location_longitude}
                          onChange={(e) => setFormData({ ...formData, location_longitude: e.target.value })}
                          placeholder="e.g. 77.2090"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Location Address</label>
                      <input
                        type="text"
                        value={formData.location_address}
                        onChange={(e) => setFormData({ ...formData, location_address: e.target.value })}
                        placeholder="e.g. 123 Main Street, City"
                      />
                    </div>
                  </>
                )}
              </section>

              {isAdmin && (
                <section className="form-section">
                  <h3 className="form-section-title">Visual accents (admin)</h3>
                  <p className="form-section-desc">Store awning decoration on category cards in the mobile app.</p>
                  <div className="form-group">
                    <label className="checkbox-inline">
                      <input
                        type="checkbox"
                        checked={formData.show_store_awning === 1}
                        onChange={(e) => setFormData({ ...formData, show_store_awning: e.target.checked ? 1 : 0 })}
                      />
                      Show store awning on category card
                    </label>
                  </div>
                </section>
              )}

              {isAdmin && (
                <section className="form-section">
                  <h3 className="form-section-title">Action button (admin)</h3>
                  <p className="form-section-desc">Optional button that opens a link.</p>
                  <div className="form-group">
                    <label className="checkbox-inline">
                      <input
                        type="checkbox"
                        checked={formData.action_button_required === 1}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            action_button_required: e.target.checked ? 1 : 0,
                          })
                        }
                      />
                      Show action button for this promotion
                    </label>
                  </div>
                  {formData.action_button_required === 1 && (
                    <>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Action Button Text</label>
                          <input
                            type="text"
                            value={formData.action_button_text}
                            onChange={(e) => setFormData({ ...formData, action_button_text: e.target.value })}
                            placeholder="e.g. Order Now"
                            maxLength={40}
                          />
                        </div>
                        <div className="form-group">
                          <label>Action Button Link (URL)</label>
                          <input
                            type="url"
                            value={formData.action_button_link}
                            onChange={(e) => setFormData({ ...formData, action_button_link: e.target.value })}
                            placeholder="https://..."
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="checkbox-inline">
                          <input
                            type="checkbox"
                            checked={formData.action_button_on_card === 1}
                            onChange={(e) => setFormData({ ...formData, action_button_on_card: e.target.checked ? 1 : 0 })}
                          />
                          Show this action button on promotion card
                        </label>
                      </div>
                    </>
                  )}
                </section>
              )}
            </>
          )}

          {/* STEP 5: Select society */}
          {steps[stepIndex]?.key === 'society' && (
            <section className="form-section">
              <h3 className="form-section-title">Step 5: Select society</h3>
              <p className="form-section-desc">
                Choose the societies where this promotion should be visible. Filter by pincode or your location, then select from the list.
              </p>
              <div className="form-row">
                <div className="form-group">
                  <label>Pincode filter</label>
                  <input
                    type="text"
                    value={societyPincodeFilter}
                    onChange={(e) => setSocietyPincodeFilter(e.target.value)}
                    placeholder="Type pincode to highlight matches"
                  />
                  <p className="form-hint">Matching societies are highlighted on the map and in the list.</p>
                </div>
                <div className="form-group">
                  <label className="checkbox-inline">
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        setSocietyFilterError(null);
                        if (e.target.checked) {
                          if (!navigator.geolocation) {
                            setSocietyFilterError('Geolocation is not supported in this browser.');
                            return;
                          }
                          navigator.geolocation.getCurrentPosition(
                            (pos) => {
                              setSocietyCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude });
                            },
                            () => setSocietyFilterError('Could not read your location.')
                          );
                        } else {
                          setSocietyCenter(null);
                        }
                      }}
                    />
                    Use my location for range filter
                  </label>
                  <label className="small" style={{ display: 'block', marginTop: 8 }}>
                    Range: {societyRangeKm} km
                  </label>
                  <input
                    type="range"
                    min={1}
                    max={30}
                    value={societyRangeKm}
                    onChange={(e) => setSocietyRangeKm(Number(e.target.value))}
                    className="form-range"
                  />
                </div>
              </div>
              {societyFilterError && <p className="form-error">{societyFilterError}</p>}
              <div className="form-group society-step-map-group">
                <label>Map</label>
                <SocietyStepMap
                  key={societyMapInstanceKey}
                  societies={societies}
                  societyPincodeFilter={societyPincodeFilter}
                  societyCenter={societyCenter}
                  societyRangeKm={societyRangeKm}
                  selectedSocietyIds={formData.society_ids}
                />
                <p className="form-hint">Hover a point for the name; click for full details. Teal = matches your filters.</p>
              </div>
              <div className="form-group">
                <label>Societies *</label>
                {societies.length === 0 ? (
                  <p style={{ color: '#999', fontStyle: 'italic' }}>No societies available. Please ensure you have access to societies.</p>
                ) : (
                  <div className="checkbox-group">
                    {customerId == null && (
                      <label key="all-society" className="checkbox-label" style={{ fontWeight: 'bold' }}>
                        <input
                          type="checkbox"
                          checked={formData.society_ids.includes(-1)}
                          onChange={() => handleSocietyToggle(-1)}
                        />
                        All Society
                      </label>
                    )}
                    {societiesForStep.map((soc) => {
                      const pinMatch =
                        societyPincodeFilter.trim().length > 0 &&
                        (soc.pincode ?? '').toString().startsWith(societyPincodeFilter.trim());
                      return (
                        <label key={soc.id} className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={formData.society_ids.includes(soc.id)}
                            onChange={() => handleSocietyToggle(soc.id)}
                            disabled={formData.society_ids.includes(-1)}
                          />
                          <span>
                            <strong>{soc.name}</strong>
                            <br />
                            <span style={{ fontSize: 12, color: '#555' }}>
                              {[soc.location, soc.city, soc.state].filter(Boolean).join(', ')}
                              {soc.pincode ? ` – ${soc.pincode}` : ''}
                            </span>
                            {pinMatch && (
                              <span style={{ marginLeft: 6, fontSize: 11, color: '#0f766e' }}>
                                (pincode match)
                              </span>
                            )}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
                {formData.society_ids.length === 0 && (
                  <p style={{ color: '#d32f2f', fontSize: '12px', marginTop: '5px' }}>Please select at least one society or &quot;All Society&quot;</p>
                )}
              </div>
            </section>
          )}

          {/* STEP 6: Category and order */}
          {steps[stepIndex]?.key === 'categoryOrder' && (
            <section className="form-section">
              <h3 className="form-section-title">Step 6: Select category and set order</h3>
              <p className="form-section-desc">
                Categories determine which section shows the promotion (for Category type). Admin can also set display order.
              </p>
              {isAdmin && (
                <div className="form-group">
                  <label>Display Order</label>
                  <input
                    type="number"
                    value={formData.display_order}
                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                  />
                  <p className="form-hint">Lower number appears first.</p>
                </div>
              )}
              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <label>Categories {formData.promotion_type === 'category' && '(choose section in app)'}</label>
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={() => setShowCreateCategory(!showCreateCategory)}
                      className="create-category-btn"
                    >
                      {showCreateCategory ? 'Cancel' : '+ Create New Category'}
                    </button>
                  )}
                </div>
                {isAdmin && showCreateCategory && (
                  <div className="create-category-form" style={{ marginBottom: '15px', padding: '15px', border: '1px solid #ddd', borderRadius: '4px' }}>
                    <input
                      type="text"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder="Category Name *"
                      style={{ width: '100%', marginBottom: '10px', padding: '8px' }}
                    />
                    <textarea
                      value={newCategoryDesc}
                      onChange={(e) => setNewCategoryDesc(e.target.value)}
                      placeholder="Description (optional)"
                      rows={2}
                      style={{ width: '100%', marginBottom: '10px', padding: '8px' }}
                    />
                    <button type="button" onClick={handleCreateCategory} className="save-category-btn">
                      Create Category
                    </button>
                  </div>
                )}
                <div className="checkbox-group">
                  {localCategories.map((cat) => (
                    <label key={cat.id} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.category_ids.includes(cat.id)}
                        onChange={() => handleCategoryToggle(cat.id)}
                      />
                      {cat.name}
                    </label>
                  ))}
                </div>
                {formData.promotion_type === 'category' && (
                  <p className="form-hint">Category promotions appear inside these sections in the app.</p>
                )}
              </div>
            </section>
          )}

          {/* STEP 7: Pricing (admin) */}
          {isAdmin && steps[stepIndex]?.key === 'pricing' && (
            <section className="form-section">
              <h3 className="form-section-title">Step 7: Set pricing (admin)</h3>
              <p className="form-section-desc">Credits deducted per view/click in the app.</p>
              <div className="form-group promotion-stat-pricing">
                <label>Credits per unit</label>
                <div className="form-row" style={{ flexWrap: 'wrap', gap: '12px' }}>
                  <div className="form-group" style={{ minWidth: '120px' }}>
                    <label className="small">Impressions (view)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.price_impressions}
                      onChange={(e) => setFormData({ ...formData, price_impressions: e.target.value })}
                      placeholder="0"
                    />
                  </div>
                  <div className="form-group" style={{ minWidth: '120px' }}>
                    <label className="small">Clicks</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.price_clicks}
                      onChange={(e) => setFormData({ ...formData, price_clicks: e.target.value })}
                      placeholder="0"
                    />
                  </div>
                  <div className="form-group" style={{ minWidth: '120px' }}>
                    <label className="small">Story views</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.price_story_views}
                      onChange={(e) => setFormData({ ...formData, price_story_views: e.target.value })}
                      placeholder="0"
                    />
                  </div>
                  <div className="form-group" style={{ minWidth: '120px' }}>
                    <label className="small">Story completes</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.price_story_completes}
                      onChange={(e) => setFormData({ ...formData, price_story_completes: e.target.value })}
                      placeholder="0"
                    />
                  </div>
                </div>
                <p className="form-hint">Credits are deducted when users view/click.</p>
              </div>
            </section>
          )}

          {/* Status (kept simple, always visible on all steps) */}
          <section className="form-section form-section-compact">
            <h3 className="form-section-title">Status</h3>
            <div className="form-group">
              <label className="checkbox-inline">
                <input
                  type="checkbox"
                  checked={formData.is_active === 1}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked ? 1 : 0 })}
                />
                Active (visible in app)
              </label>
            </div>
          </section>

          <div className="form-actions">
            <button type="button" onClick={onClose} className="cancel-button">
              Cancel
            </button>
            <div style={{ flex: 1 }} />
            <button type="button" onClick={goBack} className="cancel-button" disabled={stepIndex === 0}>
              Back
            </button>
            {stepIndex < lastStepIndex ? (
              <button type="button" onClick={goNext} className="save-button">
                Next
              </button>
            ) : (
              <button type="submit" className="save-button" disabled={loading}>
                {loading ? 'Saving...' : isEditing ? 'Update' : 'Create'}
              </button>
            )}
          </div>
        </form>
    </>
  );

  return inline ? (
    <div className="form-modal form-modal-inline">{formContent}</div>
  ) : (
    <div className="form-overlay">
      <div className="form-modal">{formContent}</div>
    </div>
  );
};

export default PromotionForm;

